import { RiArrowDownSLine, RiCheckLine } from '@remixicon/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import {
  Button,
  Label,
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  Popover,
  Select,
  SelectProps,
  SelectValue,
} from 'react-aria-components';
import { cn } from './utils';

const agSelectTriggerVariants = cva(
  'inline-flex items-center justify-between gap-2 whitespace-nowrap font-medium transition-all duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-primary disabled:pointer-events-none disabled:opacity-60 text-neutral-text',
  {
    variants: {
      variant: {
        default: 'bg-white border border-neutral-5 hover:border-neutral-6',
        ghost: 'bg-transparent hover:bg-neutral-2',
      },
      size: { sm: 'h-[32px] px-2.5 text-14', md: 'h-[40px] px-3 text-14', lg: 'h-[48px] px-4 text-16' },
      shape: { rounded: 'rounded-md', pill: 'rounded-full' },
    },
  }
);

type AgSelectVariantProps = VariantProps<typeof agSelectTriggerVariants>;

export interface AgSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface AgSelectProps<T extends object> extends Omit<SelectProps<T>, 'children'> {
  label?: string;
  /** Show red asterisk for required fields */
  isRequired?: boolean;
  options: AgSelectOption[];
  placeholder?: string;
  variant?: AgSelectVariantProps['variant'];
  size?: AgSelectVariantProps['size'];
  shape?: AgSelectVariantProps['shape'];
  className?: string;
  triggerClassName?: string;
  /** Additional classes for the dropdown listbox */
  listBoxClassName?: string;
  /** Content rendered below the options inside the dropdown */
  footer?: React.ReactNode;
}

export function AgSelect<T extends object>({
  label,
  isRequired,
  options,
  placeholder = 'Select...',
  variant = 'default',
  size = 'md',
  shape = 'rounded',
  className,
  triggerClassName,
  listBoxClassName,
  footer,
  ...props
}: AgSelectProps<T>) {
  return (
    <Select className={cn('flex flex-col gap-2', className)} {...props}>
      {label && (
        <Label className="text-14 font-medium text-neutral-text">
          {label}
          {isRequired && <span className="text-red-text ml-2">*</span>}
        </Label>
      )}
      <Button className={cn(agSelectTriggerVariants({ variant, size, shape }), triggerClassName)}>
        <SelectValue className="flex-1 text-left truncate data-[placeholder]:text-neutral-alpha-7">
          {({ selectedText }) => selectedText || placeholder}
        </SelectValue>
        <RiArrowDownSLine
          className="h-4 w-4 text-neutral-alpha-7 transition-transform duration-200 group-data-[open]:rotate-180"
          aria-hidden="true"
        />
      </Button>
      <Popover
        style={{ zIndex: 200000 }}
        className={cn(
          'min-w-[var(--trigger-width)] overflow-hidden rounded-md bg-white border border-neutral-5 shadow-sm',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <ListBox className={cn('p-1.5 outline-none max-h-[300px] overflow-auto', listBoxClassName)}>
          {options.map((option) => (
            <AgSelectItem key={option.value} id={option.value} textValue={option.label} isDisabled={option.disabled}>
              {option.label}
            </AgSelectItem>
          ))}
        </ListBox>
        {footer && <div className="border-t border-neutral-5 p-1">{footer}</div>}
      </Popover>
    </Select>
  );
}

interface AgSelectItemProps extends ListBoxItemProps {
  children: React.ReactNode;
}

export const AgSelectItem = forwardRef<HTMLDivElement, AgSelectItemProps>(({ children, className, ...props }, ref) => {
  return (
    <ListBoxItem
      ref={ref}
      className={cn(
        'relative flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md cursor-pointer outline-none text-14 text-neutral-text',
        'hover:bg-[rgba(10,15,41,0.04)] data-focused:bg-[rgba(10,15,41,0.04)]',
        'data-selected:bg-brand-alpha-1',
        'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
        className
      )}
      {...props}
    >
      {({ isSelected }) => (
        <>
          <span className="flex-1 truncate">{children}</span>
          {isSelected && <RiCheckLine className="h-4 w-4 text-neutral-alpha-8" aria-hidden="true" />}
        </>
      )}
    </ListBoxItem>
  );
});

AgSelectItem.displayName = 'AgSelectItem';
